import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Button,
  Grid,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { motion } from "framer-motion";
import { SITE_NAME, REACT_APP_SITE_URL } from "../config/seo";
import {
  generateWebPageStructuredData,
  generateBreadcrumbStructuredData,
} from "../utils/structuredData";
import { blogPosts } from "../data/blogPosts";
import type { BlogCategory } from "../data/blogPosts";
import { SEO } from "../components/SEO";
import {
  AnimatedInView,
  Section,
  GradientHero,
  GradientText,
  CTABand,
  staggerChild,
} from "../components/ui";

const PAGE_DESCRIPTION =
  "Read guides, comparisons, and tips about POS systems, billing machines, ECR machines, and receipt printers — helping retail and small business owners in India make informed decisions.";

const Blog: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>("All");

  const categories: string[] = [
    "All",
    ...Array.from(new Set(blogPosts.map((p) => p.category))),
  ];

  const filtered =
    activeCategory === "All"
      ? blogPosts
      : blogPosts.filter((p) => p.category === (activeCategory as BlogCategory));

  return (
    <>
      <SEO
        title="Blog & Guides"
        description={PAGE_DESCRIPTION}
        keywords={[
          "POS System India",
          "Billing Machine India",
          "ECR Machine Guide",
          "POS Printer India",
          "Retail Billing Guide",
        ]}
        jsonLd={[
          generateWebPageStructuredData({
            title: `Blog & Guides - ${SITE_NAME}`,
            description: PAGE_DESCRIPTION,
            url: `${REACT_APP_SITE_URL}/blog`,
          }),
          generateBreadcrumbStructuredData([
            { name: "Home", item: `${REACT_APP_SITE_URL}/` },
            { name: "Blog", item: `${REACT_APP_SITE_URL}/blog` },
          ]),
        ]}
      />

      <GradientHero
        compact
        eyebrow="Knowledge Base"
        title={
          <>
            Guides for smarter <GradientText>billing decisions</GradientText>
          </>
        }
        subtitle="Practical guides on POS systems, billing machines, and retail technology — written by the R.S. Enterprises team."
      />

      <Section py={{ xs: 4, md: 6 }}>
        {/* Category Filter */}
        <Box
          sx={{
            mb: 5,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {categories.map((cat) => (
            <Chip
              key={cat}
              label={cat}
              onClick={() => setActiveCategory(cat)}
              color={activeCategory === cat ? "primary" : "default"}
              variant={activeCategory === cat ? "filled" : "outlined"}
              sx={{
                px: 1.5,
                py: 2.2,
                fontSize: "0.9rem",
                "&:hover": { transform: "translateY(-1px)", boxShadow: 1 },
                transition: "all 0.2s",
              }}
            />
          ))}
        </Box>

        {/* Post Grid */}
        <AnimatedInView variant="stagger">
          <Grid container spacing={4}>
            {filtered.map((post) => (
              <Grid key={post.slug} size={{ xs: 12, sm: 6, md: 4 }}>
                <motion.div variants={staggerChild} style={{ height: "100%" }}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {post.coverImage ? (
                      <Box
                        sx={{
                          height: 180,
                          bgcolor: "#FFFFFF",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          p: 2,
                        }}
                      >
                        <Box
                          component="img"
                          src={post.coverImage}
                          alt={post.title}
                          loading="lazy"
                          sx={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: 120,
                          background: (theme) => theme.custom.gradients.hero,
                          opacity: 0.9,
                        }}
                      />
                    )}

                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        p: 3,
                      }}
                    >
                      <Chip
                        label={post.category}
                        size="small"
                        sx={{
                          alignSelf: "flex-start",
                          bgcolor: "rgba(199,93,44,0.1)",
                          color: "secondary.dark",
                        }}
                      />

                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {post.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          flexGrow: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {post.excerpt}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {new Date(post.publishedAt).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {post.readTime} min read
                        </Typography>
                      </Box>

                      <Button
                        component={RouterLink}
                        to={`/blog/${post.slug}`}
                        size="small"
                        endIcon={<ArrowForwardIcon />}
                        sx={{ mt: 1, alignSelf: "flex-start", fontWeight: 700 }}
                      >
                        Read Article
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </AnimatedInView>
      </Section>

      <CTABand
        title="Prefer to just ask us?"
        subtitle="Skip the research — tell us your business type and we'll recommend the right setup."
      />
    </>
  );
};

export default Blog;
